import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function PortalAlterarPasswordPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    return checks;
  };

  const passwordChecks = validatePassword(formData.newPassword);
  const allChecksPassed = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validations
    if (!formData.currentPassword) {
      setErrors(prev => ({ ...prev, currentPassword: 'Password atual é obrigatória' }));
      return;
    }

    if (!allChecksPassed) {
      setErrors(prev => ({ ...prev, newPassword: 'Password não cumpre os requisitos' }));
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setErrors(prev => ({ ...prev, confirmPassword: 'As passwords não coincidem' }));
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setErrors(prev => ({ ...prev, newPassword: 'Nova password deve ser diferente da atual' }));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/alterar-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        })
      });

      if (response.ok) {
        toast.success('Password alterada com sucesso!');
        // Update user in localStorage to remove must_change_password flag
        const storedUser = localStorage.getItem('portal_user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          user.must_change_password = false;
          localStorage.setItem('portal_user', JSON.stringify(user));
        }
        navigate('/portal/dashboard');
      } else {
        const data = await response.json();
        if (data.detail === 'Password atual incorreta') {
          setErrors({ currentPassword: 'Password atual incorreta' });
        } else {
          toast.error(data.detail || 'Erro ao alterar password');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao alterar password');
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPassword = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4" data-testid="alterar-password-page">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <ShieldCheck className="w-8 h-8 text-[#0066CC]" />
          </div>
          <CardTitle className="text-2xl">Alterar Password</CardTitle>
          <CardDescription>
            Por segurança, deve alterar a sua password temporária.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Password Atual</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="currentPassword"
                  type={showPasswords.current ? 'text' : 'password'}
                  value={formData.currentPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className={`pl-10 pr-10 ${errors.currentPassword ? 'border-red-500' : ''}`}
                  placeholder="Introduza a password atual"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => toggleShowPassword('current')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.currentPassword}
                </p>
              )}
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword">Nova Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="newPassword"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className={`pl-10 pr-10 ${errors.newPassword ? 'border-red-500' : ''}`}
                  placeholder="Introduza a nova password"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => toggleShowPassword('new')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.newPassword}
                </p>
              )}
            </div>

            {/* Password Requirements */}
            {formData.newPassword && (
              <div className="bg-slate-50 p-3 rounded-lg space-y-1">
                <p className="text-xs font-medium text-slate-600 mb-2">Requisitos da password:</p>
                <PasswordCheck label="Mínimo 8 caracteres" passed={passwordChecks.length} />
                <PasswordCheck label="Pelo menos 1 maiúscula" passed={passwordChecks.uppercase} />
                <PasswordCheck label="Pelo menos 1 minúscula" passed={passwordChecks.lowercase} />
                <PasswordCheck label="Pelo menos 1 número" passed={passwordChecks.number} />
                <PasswordCheck label="Pelo menos 1 carácter especial" passed={passwordChecks.special} />
              </div>
            )}

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirmar Nova Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="confirmPassword"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={`pl-10 pr-10 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  placeholder="Confirme a nova password"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => toggleShowPassword('confirm')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-sm text-red-500 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.confirmPassword}
                </p>
              )}
              {formData.confirmPassword && formData.newPassword === formData.confirmPassword && (
                <p className="text-sm text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Passwords coincidem
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-[#0066CC] hover:bg-[#0055AA]"
              disabled={loading || !allChecksPassed || formData.newPassword !== formData.confirmPassword}
              data-testid="submit-password-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  A alterar...
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4 mr-2" />
                  Alterar Password
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function PasswordCheck({ label, passed }) {
  return (
    <div className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-slate-400'}`}>
      {passed ? (
        <CheckCircle className="w-3 h-3" />
      ) : (
        <div className="w-3 h-3 rounded-full border border-slate-300" />
      )}
      {label}
    </div>
  );
}
